import { LOGIN_REF, REGISTER_REF } from '@common/constants'
import { GENERIC_ERROR_MESSAGE, parseErrorResponse } from '@common/utils'
import { captureException } from '@sentry/node'
import EnterPhoneInput from '@components/auth/phone-input'
import { requestLoginOtp } from '@containers/auth/utils'
import { validateIdentifier } from '@containers/common/validators'
import useSegment, { SegmentEventEnum } from '@hooks/useSegment'
import { isEmptyObject } from '@lib/utils/common'
import { USER_COUNTRY_FROM_IP_COOKIE } from '@lib/constants/common'
import {
  buildIdentifierBody,
  IdentifierMode,
  OtpRequestBody,
} from '@lib/utils/identifier'
import { encodePhoneQueryValue } from '@lib/utils/phone-query'
import Router from 'next/router'
import { parseCookies } from 'nookies'
import React, { useState } from 'react'

type Props = {
  next: string
}

type Query = {
  next?: string
  ph?: string
  source?: string
}

const LoginFlow = ({ next }: Props) => {
  const track = useSegment()
  const [loginData, setLoginData] = useState({
    // User input. `phone` holds whichever identifier was typed - a phone number
    // (dial code, no plus) or an email address; `mode` says which.
    phone: '',
    mode: 'phone' as IdentifierMode,
    otp: '',

    // Server state and response
    response: {},
    loading: false,
    errors: {},
  })

  const changeLoginData = (data: any) => {
    setLoginData((prev) => ({ ...prev, ...data }))
  }

  // OTP based login
  const handlePhoneInputChange = (value: string, mode: IdentifierMode) => {
    changeLoginData({
      phone: value,
      mode,
    })
  }

  const handlePhoneInputFormSubmit = async () => {
    if (loginData.loading) return

    changeLoginData({
      loading: true,
    })

    const validationErrors = validateIdentifier(loginData.phone, loginData.mode)
    if (!isEmptyObject(validationErrors)) {
      changeLoginData({ loading: false, errors: validationErrors })
      return
    }

    const formBody: OtpRequestBody = buildIdentifierBody(
      loginData.phone,
      loginData.mode
    )
    const countryFromIP = parseCookies()?.[USER_COUNTRY_FROM_IP_COOKIE]
    if (countryFromIP) {
      formBody.country = countryFromIP.toUpperCase()
    }
    // `loading` clears in `finally` so a throw anywhere below still resets the
    // button - it used to spin forever when parsing a non-JSON error threw.
    try {
      const result = await requestLoginOtp(formBody)

      const currentQuery = Router.query
      const query: Query = { ...currentQuery }
      const encodedPhone = encodePhoneQueryValue(loginData.phone)
      if (encodedPhone) query.ph = encodedPhone
      if (next) query.next = next

      if (result.status === 200) {
        track(SegmentEventEnum.LOGIN_CLICK_LOGIN_EXISTING_USER)

        query.source = LOGIN_REF

        Router.push({
          pathname: '/login/verify-otp/',
          query,
        })
      } else if (result.status === 204) {
        track(SegmentEventEnum.LOGIN_CLICK_LOGIN_NEW_USER)
        query.source = REGISTER_REF

        Router.push({
          pathname: '/register/',
          query,
        })
      } else {
        changeLoginData({ errors: await parseErrorResponse(result) })
      }
    } catch (error) {
      // Usually the request never completed - dropped connection, DNS, offline.
      // But this wraps the whole handler, so a genuine bug in here would look
      // the same to the user. Report it rather than letting the generic message
      // bury it.
      captureException(error)
      changeLoginData({ errors: { detail: GENERIC_ERROR_MESSAGE } })
    } finally {
      changeLoginData({ loading: false })
    }
  }
  const redirectToPasswordPage = () => {
    const currentQuery = Router.query
    const query: Query = { ...currentQuery }
    const encodedPhone = encodePhoneQueryValue(loginData.phone)
    if (encodedPhone) query.ph = encodedPhone
    if (next) query.next = next
    Router.push({
      pathname: '/login/password/',
      query,
    })
  }

  return (
    <EnterPhoneInput
      phone={loginData.phone}
      mode={loginData.mode}
      loading={loginData.loading}
      errors={loginData.errors}
      handlePhoneInputChange={handlePhoneInputChange}
      handlePhoneInputFormSubmit={handlePhoneInputFormSubmit}
      redirectToPasswordPage={redirectToPasswordPage}
    />
  )
}

export default LoginFlow
